import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import messages from '../../assets/Messages.json';
import axios from 'axios';
import { API_URL } from '../../Constant';
import { showLoading } from 'react-global-loading';

// Helper function to mark a page as saved (green in header)
// Call this after successful save in any balance sheet page
export const markPageAsSaved = (pageName) => {
  try {
    const savedPages = JSON.parse(localStorage.getItem('balanceSheetSavedPages') || '[]');
    if (!savedPages.includes(pageName)) {
      savedPages.push(pageName);
      localStorage.setItem('balanceSheetSavedPages', JSON.stringify(savedPages));
    }
    return true;
  } catch (error) {
    console.error('Error updating saved pages:', error);
    return false;
  }
};

// Helper function to clear all saved pages (use on logout or reset)
export const clearSavedPages = () => {
  try {
    localStorage.removeItem('balanceSheetSavedPages');
    localStorage.removeItem('balanceSheetAccessPageIds');
    localStorage.removeItem('balanceSheetApiCalled');
    return true;
  } catch (error) {
    console.error('Error clearing saved pages:', error);
    return false;
  }
};

// Helper function to get auth config (for use in refreshBalanceSheetValidation)
const getAuthConfig = () => {
  let token = localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('userToken');

  if (!token) {
    token = sessionStorage.getItem('token') ||
      sessionStorage.getItem('jwt') ||
      sessionStorage.getItem('accessToken');
  }

  if (!token) {
    return null;
  }

  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  };
};

// Helper function to refresh balance sheet validation
// Call this after successful save in any balance sheet page
export const refreshBalanceSheetValidation = async (dslNo, surveyYear) => {
  try {
    const currentConfig = getAuthConfig();
    if (!currentConfig) {
      console.error('Authentication token not found for balance sheet validation');
      return false;
    }

    // Extract only year part (e.g., "2024" from "2024-25")
    const yearOnly = surveyYear ? String(surveyYear).split('-')[0] : surveyYear;

    const payload = {
      dsl_no: dslNo,
      survey_year: yearOnly
    };

    const response = await axios.post(`${API_URL}api/identification-particulars/blockValidationbalanceSheet`, payload, currentConfig);

    if (response.data && response.data.status) {
      const { data } = response.data;
      // API returns allblockData array of page IDs
      const accessIds = data.allblockData || [];

      if (Array.isArray(accessIds) && accessIds.length > 0) {
        const numericIds = accessIds.map(id => Number(id));
        localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify(numericIds));
        localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark API as called
        // Trigger a custom event to notify BalanceSheetHeader to update
        window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: numericIds }));
        return true;
      } else {
        // If allblockData is empty, set only first page accessible
        localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
        localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark API as called
        // Clear flags that enable all pages
        localStorage.removeItem('balanceSheetDataLoaded');
        localStorage.removeItem('balanceSheetDataSaved');
        // Trigger event with only first page
        window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: [1] }));
        return true;
      }
    }
    // If API returns false status, clear API called flag
    localStorage.removeItem('balanceSheetApiCalled');
    localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
    window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: [1] }));
    return false;
  } catch (error) {
    console.error('Error refreshing balance sheet validation:', error);
    // On error, clear API called flag
    localStorage.removeItem('balanceSheetApiCalled');
    localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
    window.dispatchEvent(new CustomEvent('balanceSheetValidationUpdated', { detail: [1] }));
    return false;
  }
};

// Page names constants for easy reference
export const PAGE_NAMES = {
  GENERAL_INFORMATION: 'GeneralInformation',
  FIXED_ASSETS: 'FixedAssets',
  BALANCE_SHEET_DATA: 'BalanceSheetData',
  PROFIT_AND_LOSS: 'ProfitAndLossAccount',
  EMPLOYMENT_LABOUR_COST: 'EmploymentAndLabourCost',
  INPUT_ITEMS_INDIGENOUS: 'InputItemsIndigenous',
  INPUT_ITEMS_IMPORTED: 'InputItemsImported',
  PRODUCTS_BY_PRODUCTS: 'ProductsAndByProducts',
  ICT_ENERGY: 'ICTUsageAndEnergyConservation',
  FIELD_OPERATIONS: 'ParticularsOfFieldOperations',
  PART_A: 'PartA',
  PART_2: 'Part2',
  FINAL_CHECKLIST: 'FinalCheckList',
  VALIDATION: 'Validation',
  REPORTS: 'Reports'
};

const balanceSheetPages = [
  { id: 1, name: 'GeneralInformation', title: 'Block A-B General Information', route: '/balanceSheet' },
  { id: 2, name: 'FixedAssets', title: 'Block C Fixed Assets Data', route: '/fixedAssets' },
  { id: 3, name: 'BalanceSheetData', title: 'Balance Sheet', route: '/balanceSheetData' },
  { id: 4, name: 'ProfitAndLossAccount', title: 'Profit and Loss Account', route: '/profitAndLossAccount' },
  { id: 5, name: 'EmploymentAndLabourCost', title: 'Block E', route: '/employmentAndLabourCost' },
  { id: 6, name: 'InputItemsIndigenous', title: 'Block H: Input Items', route: '/input-items' },
  { id: 7, name: 'InputItemsImported', title: 'Block I: Input Items', route: '/input-items-imported' },
  { id: 8, name: 'ProductsAndByProducts', title: 'Block J: Manufactured by unit', route: '/products-and-by-products' },
  { id: 9, name: 'ICTUsageAndEnergyConservation', title: 'Block KL: IT/Energy Conservation', route: '/ict-usage-energy-conservation' },
  { id: 10, name: 'PartA', title: 'Part A-1', route: '/part-a' },
  { id: 11, name: 'FinalCheckList', title: 'Final Checklist', route: '/final-checklist' },

  // { id: 10, name: 'ParticularsOfFieldOperations', title: 'Particulars of Field Operations', route: '/particulars-of-field-operations' },
  // { id: 12, name: 'Part2', title: 'Part-2', route: '/part2' },
  // { id: 14, name: 'Validation', title: 'Validation', route: '/validation' },
  // { id: 15, name: 'Reports', title: 'Reports', route: '/report-selection' },
];

export default function BalanceSheetHeader({ pageInfo }) {
  const location = useLocation();
  const toastShownRef = useRef({}); // Track which toast messages have been shown
  const [accessPageIds, setAccessPageIds] = useState([1]); // Store accessible page IDs from API - default to only first page
  const [apiCalled, setApiCalled] = useState(false); // Track if API has been called successfully
  const blockValidationRef = useRef({ isFetching: false, lastPayload: null });

  // API configuration - using dynamic token with multiple fallbacks (local function for component)
  const getAuthConfigLocal = () => {
    let token = localStorage.getItem('token') ||
      localStorage.getItem('jwt') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('userToken');

    if (!token) {
      token = sessionStorage.getItem('token') ||
        sessionStorage.getItem('jwt') ||
        sessionStorage.getItem('accessToken');
    }

    if (!token) {
      console.error('No token found in localStorage or sessionStorage');
      return null;
    }

    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    };
  };

  // Balance Sheet Validation API call - similar to blockValidation
  const balanceSheetValidation = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for balance sheet validation');
      return;
    }

    // Create a unique key for the payload
    const postData = {
      dsl_no: dslNo,
      survey_year: surveyYear
    };
    const payloadKey = JSON.stringify(postData);

    // Check if same payload is already being fetched
    if (blockValidationRef.current.isFetching && blockValidationRef.current.lastPayload === payloadKey) {
      return;
    }

    // Check if same payload was just fetched
    if (blockValidationRef.current.lastPayload === payloadKey && !blockValidationRef.current.isFetching) {
      return;
    }

    try {
      blockValidationRef.current.isFetching = true;
      blockValidationRef.current.lastPayload = payloadKey;

      const currentConfig = getAuthConfigLocal();
      if (!currentConfig) {
        console.error('Authentication token not found for balance sheet validation');
        blockValidationRef.current.isFetching = false;
        return;
      }

      // Extract only year part (e.g., "2024" from "2024-25")
      const yearOnly = surveyYear ? String(surveyYear).split('-')[0] : surveyYear;

      const payload = {
        dsl_no: dslNo,
        survey_year: yearOnly
      };

      const response = await axios.post(`${API_URL}api/identification-particulars/blockValidationbalanceSheet`, payload, currentConfig);

      if (response.data && response.data.status) {
        const { data } = response.data;
        // API returns allblockData array of page IDs
        const accessIds = data.allblockData || [];

        // Mark API as called successfully
        setApiCalled(true);

        if (Array.isArray(accessIds) && accessIds.length > 0) {
          // Convert to numbers if they're strings
          const numericIds = accessIds.map(id => Number(id));
          setAccessPageIds(numericIds);
          // Store in localStorage for persistence
          localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify(numericIds));
          localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark that API was called
        } else {
          // If allblockData is empty, set only first page accessible
          setAccessPageIds([1]);
          localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
          localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark that API was called
          // Clear flags that enable all pages when no data is available
          localStorage.removeItem('balanceSheetDataLoaded');
          localStorage.removeItem('balanceSheetDataSaved');
        }
      } else {
        // If API returns false status, set default accessible pages
        setAccessPageIds([1]);
        localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
        localStorage.setItem('balanceSheetApiCalled', 'true'); // Mark that API was called
      }
    } catch (error) {
      console.error('Error fetching balance sheet validation:', error);
      // On error, set only first page accessible (grey for others)
      setAccessPageIds([1]);
      setApiCalled(false); // Mark API as not called successfully
      localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
      localStorage.removeItem('balanceSheetApiCalled'); // Clear API called flag
    } finally {
      blockValidationRef.current.isFetching = false;
    }
  }, []);

  // Fetch validation data on component mount
  useEffect(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    const apiCalledFlag = localStorage.getItem('balanceSheetApiCalled') === 'true';

    if (balanceSheetData) {
      try {
        const parsedData = JSON.parse(balanceSheetData);
        const dslNo = parsedData.dslNo || parsedData.dsl_no;
        const surveyYear = parsedData.year || parsedData.surveyYear || parsedData.survey_year;

        if (dslNo && surveyYear) {
          // Always call API to get fresh data
          balanceSheetValidation(dslNo, surveyYear);
        } else {
          // No DSL/Year - only load from localStorage if API was previously called
          if (apiCalledFlag) {
            const storedIds = localStorage.getItem('balanceSheetAccessPageIds');
            if (storedIds) {
              try {
                const parsedIds = JSON.parse(storedIds);
                setAccessPageIds(parsedIds);
                setApiCalled(true);
              } catch (e) {
                setAccessPageIds([1]);
                setApiCalled(false);
              }
            } else {
              setAccessPageIds([1]);
              setApiCalled(false);
            }
          } else {
            // API not called - keep only first page accessible (grey for others)
            setAccessPageIds([1]);
            setApiCalled(false);
          }
        }
      } catch (error) {
        console.error('Error parsing balanceSheetFormData:', error);
        setAccessPageIds([1]);
        setApiCalled(false);
      }
    } else {
      // No saved data - only load from localStorage if API was previously called
      if (apiCalledFlag) {
        const storedIds = localStorage.getItem('balanceSheetAccessPageIds');
        if (storedIds) {
          try {
            const parsedIds = JSON.parse(storedIds);
            setAccessPageIds(parsedIds);
            setApiCalled(true);
          } catch (e) {
            setAccessPageIds([1]);
            setApiCalled(false);
          }
        } else {
          setAccessPageIds([1]);
          setApiCalled(false);
        }
      } else {
        // API not called - keep only first page accessible (grey for others)
        setAccessPageIds([1]);
        setApiCalled(false);
      }
    }
  }, [balanceSheetValidation]);

  // Listen for reset event to clear header colors
  useEffect(() => {
    const handleRefreshHeader = () => {
      // Reset access page IDs to default (only first page accessible)
      setAccessPageIds([1]);
      setApiCalled(false); // Reset API called flag
      localStorage.setItem('balanceSheetAccessPageIds', JSON.stringify([1]));
      localStorage.removeItem('balanceSheetApiCalled'); // Clear API called flag
    };

    window.addEventListener('refreshBalanceSheetHeader', handleRefreshHeader);

    return () => {
      window.removeEventListener('refreshBalanceSheetHeader', handleRefreshHeader);
    };
  }, []);

  // Listen for validation updates from other components
  useEffect(() => {
    const handleValidationUpdate = (event) => {
      if (event.detail && Array.isArray(event.detail)) {
        setAccessPageIds(event.detail);
        setApiCalled(true); // Mark API as called when updated from other components
        localStorage.setItem('balanceSheetApiCalled', 'true');
      }
    };

    window.addEventListener('balanceSheetValidationUpdated', handleValidationUpdate);
    return () => {
      window.removeEventListener('balanceSheetValidationUpdated', handleValidationUpdate);
    };
  }, []);

  const getPageNameFromRoute = (route) => {
    const routeMap = {
      '/balanceSheet': 'GeneralInformation',
      '/fixedAssets': 'FixedAssets',
      '/balanceSheetData': 'BalanceSheetData',
      '/profitAndLossAccount': 'ProfitAndLossAccount',
      '/employmentAndLabourCost': 'EmploymentAndLabourCost',
      '/input-items': 'InputItemsIndigenous',
      '/input-items-imported': 'InputItemsImported',
      '/products-and-by-products': 'ProductsAndByProducts',
      '/ict-usage-energy-conservation': 'ICTUsageAndEnergyConservation',
      // '/particulars-of-field-operations': 'ParticularsOfFieldOperations',
      '/part-a': 'PartA',
      '/part2': 'Part2',
      '/final-checklist': 'FinalCheckList',
      '/validation': 'Validation',
      '/report-selection': 'Reports',
    };
    return routeMap[route] || '';
  };

  const currentPageName = getPageNameFromRoute(location.pathname);
  const currentPageIndex = balanceSheetPages.findIndex(page => page.name === currentPageName);

  const processBar = (item, index) => {
    const isActive = currentPageName === item.name;
    const isAccessible = accessPageIds.includes(item.id);

    // Green color will ONLY appear after API call - based strictly on accessPageIds from API response
    // If API hasn't been called, keep everything grey except first page
    let tempActive = false;
    if (apiCalled && accessPageIds.length > 0) {
      const availablePageIds = balanceSheetPages
        .filter(page => accessPageIds.includes(page.id))
        .map(page => page.id);

      // Green pages count = n - 1 (where n is the count from API response)
      const greenPagesCount = accessPageIds.length - 1;

      // Get the first (count - 1) available pages for green color
      const greenPageIds = availablePageIds.slice(0, greenPagesCount);
      tempActive = greenPageIds.includes(item.id);
    }

    // Navigation accessibility - only based on API response (accessPageIds) and API must be called
    const isAccessibleForNavigation = apiCalled && isAccessible;

    return (
      isActive ? (
        <li key={item.id} className="current">
          <Link to={item.route}>
            <span>{index + 1}</span>
            <strong>{item.title}</strong>
          </Link>
        </li>
      ) : isAccessibleForNavigation ? (
        <li className={tempActive ? "active" : ""} key={item.id}>
          <Link to={item.route}>
            <span>{index + 1}</span>
            <strong>{item.title}</strong>
          </Link>
        </li>
      ) : (
        <li key={item.id}>
          <Link to="#" onClick={(e) => {
            e.preventDefault();
            const toastKey = `page-locked-${item.id}`;
            if (!toastShownRef.current[toastKey]) {
              toast.error(`Please complete previous pages first`, messages.COLOR_RED);
              toastShownRef.current[toastKey] = true;
              setTimeout(() => delete toastShownRef.current[toastKey], 3000);
            }
          }}>
            <span>{index + 1}</span>
            <strong>{item.title}</strong>
          </Link>
        </li>
      )
    );
  };

  return (
    <div className="card mx-3 mb-3">
      <div className="card-body pb-1">
        <div className="row align-items-center">
          <ul id="progressbar" className="mb-3" style={{ width: '100%' }}>
            {balanceSheetPages.map((item, index) => processBar(item, index))}
          </ul>
        </div>
      </div>
      <style>
        {`
          #progressbar li {
            width: ${100 / balanceSheetPages.length}% !important;
          }
        `}
      </style>
    </div>
  );
}

